/**
 * ReferralReportsPage
 * Page for managing referral exam reports (laudos terceirizados)
 * Groups exams by referral (clinic) for better organization
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Chip,
  Button,
  CircularProgress,
  Tooltip,
  LinearProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  CloudUpload,
  Download,
  Delete,
  ArrowBack,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import {
  PageShell,
  DataTable,
  HelpButton,
  ActionButton,
  ActionIconButton,
  type DataTableColumn,
} from '../../components';
import appointmentService from '../../services/appointmentService';
import appointmentExamService from '../../services/appointmentExamService';
import type { Appointment } from '../../types/appointment';
import {
  AppointmentStatus,
  APPOINTMENT_STATUS_LABELS,
  APPOINTMENT_STATUS_COLORS,
} from '../../types/appointment';
import type { AppointmentExam } from '../../types/appointmentExam';
import {
  REFERRAL_STATUS_LABELS,
  REFERRAL_STATUS_COLORS,
  ReferralStatus,
} from '../../types/appointmentExam';
import type { AppointmentReferral } from '../../types/appointmentReferral';

/**
 * Mexer no arquivo do laudo é ação de duas mãos: vale para quem administra o agendamento e
 * para quem administra o laudo terceirizado. É o mesmo `qualquer` do `pode()` da rota.
 */
const GERIR_LAUDO = ['appointment.update', 'referral_report.manage'] as const;

export const ReferralReportsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const appointmentId = Number(id);

  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [exams, setExams] = useState<AppointmentExam[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  const fetchData = useCallback(async () => {
    try {
      const [appt, referralExams] = await Promise.all([
        appointmentService.getAppointmentById(appointmentId),
        appointmentExamService.getReferralExams(appointmentId),
      ]);
      setAppointment(appt);
      setExams(referralExams);
    } catch {
      setSnackbar({ open: true, message: 'Erro ao carregar dados', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [appointmentId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUploadClick = (examId: number) => {
    fileInputRefs.current[examId]?.click();
  };

  const handleFileChange = async (examId: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(examId);
    try {
      await appointmentExamService.uploadFile(appointmentId, examId, file);
      const [appt, referralExams] = await Promise.all([
        appointmentService.getAppointmentById(appointmentId),
        appointmentExamService.getReferralExams(appointmentId),
      ]);
      setAppointment(appt);
      setExams(referralExams);

      const allHaveFiles = referralExams.every(e => e.filePath != null);
      if (allHaveFiles && appt.status === AppointmentStatus.CONCLUIDO) {
        setSnackbar({
          open: true,
          message: 'Todos os laudos anexados! Agendamento concluído.',
          severity: 'success',
        });
      } else {
        setSnackbar({ open: true, message: 'Laudo anexado com sucesso', severity: 'success' });
      }
    } catch {
      setSnackbar({ open: true, message: 'Erro ao enviar laudo', severity: 'error' });
    } finally {
      setUploading(null);
      event.target.value = '';
    }
  };

  const handleDelete = async (examId: number) => {
    try {
      await appointmentExamService.deleteFile(appointmentId, examId);
      await fetchData();
      setSnackbar({ open: true, message: 'Laudo removido', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Erro ao remover laudo', severity: 'error' });
    }
  };

  const handleDownload = (examId: number) => {
    const url = appointmentExamService.getDownloadUrl(appointmentId, examId);
    window.open(url, '_blank');
  };

  const breadcrumbs = [
    { label: 'Início', path: '/' },
    { label: 'Agendamentos', path: '/appointments' },
    { label: `#${appointmentId}`, path: `/appointments/${appointmentId}` },
    { label: 'Laudos Terceirizados' },
  ];

  if (loading) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title="Laudos de Exames Terceirizados">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  if (!appointment) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title="Laudos de Exames Terceirizados">
        <Alert severity="error">Agendamento n&atilde;o encontrado</Alert>
      </PageShell>
    );
  }

  const totalExams = exams.length;
  const completedExams = exams.filter(e => e.filePath != null).length;
  const progress = totalExams > 0 ? (completedExams / totalExams) * 100 : 0;

  // Group exams by referral (clinic) if referrals are available
  const referrals = appointment.referrals || [];
  // Build a map from referralId to referral for lookup
  const referralMap = new Map<number, AppointmentReferral>();
  for (const r of referrals) {
    referralMap.set(r.id, r);
  }

  // Group exams by referral (using referralId)
  const groupedByClinic = new Map<string, { clinicName: string; referral?: AppointmentReferral; exams: AppointmentExam[] }>();
  for (const exam of exams) {
    const key = String(exam.referralId ?? 'unknown');
    if (!groupedByClinic.has(key)) {
      const clinicName = exam.referral?.clinic?.tradeName || exam.referral?.clinic?.legalName || `Clínica #${exam.referral?.clinicId ?? '?'}`;
      // Find matching referral
      const matchingReferral = referrals.find(r => r.id === exam.referralId);
      groupedByClinic.set(key, { clinicName, referral: matchingReferral, exams: [] });
    }
    groupedByClinic.get(key)!.exams.push(exam);
  }

  const renderExamActions = (exam: AppointmentExam) => {
    if (uploading === exam.id) {
      return <CircularProgress size={24} />;
    }

    if (exam.filePath) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
          <Tooltip title="Download">
            <IconButton size="small" color="primary" onClick={() => handleDownload(exam.id)}>
              <Download fontSize="small" />
            </IconButton>
          </Tooltip>
          <ActionIconButton
            acao={GERIR_LAUDO}
            rotulo="Remover laudo"
            size="small"
            color="error"
            onClick={() => handleDelete(exam.id)}
          >
            <Delete fontSize="small" />
          </ActionIconButton>
          <ActionIconButton
            acao={GERIR_LAUDO}
            rotulo="Substituir laudo"
            size="small"
            color="warning"
            onClick={() => handleUploadClick(exam.id)}
          >
            <CloudUpload fontSize="small" />
          </ActionIconButton>
        </Box>
      );
    }

    return (
      <ActionButton
        acao={GERIR_LAUDO}
        rotulo="Enviar laudo"
        size="small"
        variant="contained"
        startIcon={<CloudUpload />}
        onClick={() => handleUploadClick(exam.id)}
      >
        Upload
      </ActionButton>
    );
  };

  const examColumns: DataTableColumn<AppointmentExam>[] = [
    {
      id: 'exam',
      label: 'Exame',
      primary: true,
      render: (exam) => exam.occupationalExam?.examName || `Exame #${exam.occupationalExamId}`,
    },
    {
      id: 'status',
      label: 'Status',
      render: (exam) =>
        exam.referral?.status ? (
          <Chip
            label={REFERRAL_STATUS_LABELS[exam.referral.status as ReferralStatus] || exam.referral.status}
            color={REFERRAL_STATUS_COLORS[exam.referral.status as ReferralStatus] || 'default'}
            size="small"
          />
        ) : null,
    },
    {
      id: 'file',
      label: 'Laudo',
      render: (exam) =>
        exam.filePath ? (
          <Chip label={exam.originalFileName || 'Arquivo anexado'} color="success" size="small" variant="outlined" />
        ) : (
          <Chip label="Pendente" color="warning" size="small" variant="outlined" />
        ),
    },
    {
      id: 'actions',
      label: 'A\u00e7\u00f5es',
      align: 'center',
      render: (exam) => (
        <>
          {renderExamActions(exam)}
          <input
            type="file"
            hidden
            ref={(el) => { fileInputRefs.current[exam.id] = el; }}
            onChange={(e) => handleFileChange(exam.id, e)}
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          />
        </>
      ),
    },
  ];

  return (
    <PageShell
      breadcrumbs={breadcrumbs}
      title="Laudos de Exames Terceirizados"
      actions={
        <>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/appointments')}
            variant="outlined"
            size="small"
          >
            Voltar
          </Button>
          <HelpButton entity="appointments" screen="laudos" size="small" />
        </>
      }
    >
        {/* Appointment Info */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Funcion&aacute;rio</Typography>
              <Typography variant="body2">{appointment.employee?.name || '-'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Empresa</Typography>
              <Typography variant="body2">{appointment.company?.tradeName || appointment.company?.legalName || '-'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Tipo de Exame</Typography>
              <Typography variant="body2">{appointment.examType}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Data</Typography>
              <Typography variant="body2">{appointmentService.formatDate(appointment.selectedDate)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Status</Typography>
              <Box>
                <Chip
                  label={APPOINTMENT_STATUS_LABELS[appointment.status as AppointmentStatus] || appointment.status}
                  color={APPOINTMENT_STATUS_COLORS[appointment.status as AppointmentStatus] || 'default'}
                  size="small"
                />
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Progress */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Typography variant="body2" fontWeight="bold">
              Progresso: {completedExams}/{totalExams} laudos anexados
            </Typography>
            {completedExams === totalExams && totalExams > 0 && (
              <Chip label="Completo" color="success" size="small" />
            )}
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            color={completedExams === totalExams ? 'success' : 'warning'}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Paper>

        {/* Exams grouped by clinic */}
        {groupedByClinic.size > 0 ? (
          Array.from(groupedByClinic.entries()).map(([key, group]) => (
            <Paper key={key} sx={{ mb: 2 }}>
              <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {group.clinicName}
                </Typography>
                {group.referral && (
                  <Chip
                    label={REFERRAL_STATUS_LABELS[group.referral.status as ReferralStatus] || group.referral.status}
                    color={REFERRAL_STATUS_COLORS[group.referral.status as ReferralStatus] || 'default'}
                    size="small"
                  />
                )}
                {group.referral?.guidePdfPath && (
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<Download />}
                    href={`/api/v1/files/${encodeURIComponent(group.referral.guidePdfPath)}`}
                    target="_blank"
                  >
                    Guia
                  </Button>
                )}
              </Box>
              <DataTable
                columns={examColumns}
                rows={group.exams}
                getRowId={(exam) => exam.id}
                emptyState={{
                  title: 'Nenhum exame terceirizado encontrado',
                  description:
                    'São os exames deste encaminhamento, enviados à clínica parceira junto com a guia.',
                }}
              />
            </Paper>
          ))
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              Nenhum exame terceirizado encontrado
            </Typography>
          </Paper>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        >
          <Alert
            onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
    </PageShell>
  );
};

export default ReferralReportsPage;
